import { useMutation } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import TableUI from "../../../../../components/Table";
import { Button, Modal } from "../../../../../components/ui";
import {
  ID_HEADER,
  INACTIVE_FLAG,
  getMutationError,
  pickInputFields,
  sortById,
} from "../helpers";
import AddRecordForm from "./AddRecordForm";
import DeleteRecordButton from "./DeleteRecordButton";
import Panel from "./Panel";

const EDITABLE_FIELDS = ["id", "description", "inactive"];

const getDeleteHeader = ({
  deleteLabel,
  deleteRecord,
  getDeleteDescription,
}) => ({
  label: "",
  name: "delete",
  type: "custom",
  width: "60px",
  component: ({ values }) => (
    <DeleteRecordButton
      getDescription={getDeleteDescription}
      onDelete={deleteRecord}
      record={values}
      title={deleteLabel}
    />
  ),
});

const getActiveRecordHeaders = ({ extraHeaders, deleteHeader }) => [
  ID_HEADER,
  {
    label: "Description",
    name: "description",
    type: "input",
    inputType: "text",
  },
  {
    label: "Active",
    name: "inactive",
    type: "input",
    inputType: "toggle",
    toggleOnValue: INACTIVE_FLAG.ACTIVE,
    toggleOffValue: INACTIVE_FLAG.INACTIVE,
    width: "100px",
  },
  ...extraHeaders,
  deleteHeader,
];

const ActiveRecordsPanel = props => {
  const {
    addLabel,
    createMutation,
    createMutationName,
    deleteLabel,
    deleteMutation,
    deleteMutationName,
    extraActions,
    extraHeaders = [],
    getCreateInput,
    getDeleteDescription,
    loading,
    notice,
    records,
    refetchQueries,
    title,
    updateMutation,
    updateMutationName,
  } = props || {};
  const [error, setError] = useState(null);

  // Updates are merged into the Apollo cache from the mutation response;
  // refetching the list mid-edit resets rows the user is typing in.
  const [update] = useMutation(updateMutation);
  const [create, { loading: creating }] = useMutation(createMutation, {
    refetchQueries,
  });
  const [destroy] = useMutation(deleteMutation, { refetchQueries });

  const deleteRecord = useCallback(
    async record => {
      const result = await destroy({ variables: { input: { id: record.id } } });
      return getMutationError(result, deleteMutationName);
    },
    [destroy, deleteMutationName],
  );

  // Table rows re-send pending edits whenever these identities change.
  const headers = useMemo(
    () =>
      getActiveRecordHeaders({
        extraHeaders,
        deleteHeader: getDeleteHeader({
          deleteLabel,
          deleteRecord,
          getDeleteDescription,
        }),
      }),
    [extraHeaders, deleteLabel, deleteRecord, getDeleteDescription],
  );
  const updateRecord = useCallback(
    async ({ variables }) => {
      const result = await update({
        variables: {
          input: pickInputFields(variables?.input, EDITABLE_FIELDS),
        },
      });
      setError(getMutationError(result, updateMutationName));
      return result;
    },
    [update, updateMutationName],
  );
  const setMutationErrorMessage = useCallback(
    mutationError => setError(mutationError?.message),
    [],
  );

  const createRecord = async values => {
    const result = await create({
      variables: { input: getCreateInput(values) },
    });
    return getMutationError(result, createMutationName);
  };

  const modalProps = {
    renderBody: ({ closeModal }) => (
      <AddRecordForm
        closeModal={closeModal}
        loading={creating}
        onSubmit={createRecord}
        title={addLabel}
      />
    ),
    closeOnBackdrop: true,
    hideCloseButton: true,
    scale: "md",
  };

  const actions = (
    <>
      {extraActions}
      <Modal {...modalProps}>
        <Button label={addLabel} action="black" />
      </Modal>
    </>
  );

  return (
    <Panel title={title} actions={actions} error={error}>
      {notice}
      <div className="overflow-x-auto">
        <TableUI
          headers={headers}
          loading={loading}
          rows={sortById(records)}
          sequentialRowUpdates
          updateMutation={updateRecord}
          variant="dynamicHeight"
          onMutationError={setMutationErrorMessage}
        />
      </div>
    </Panel>
  );
};

export default ActiveRecordsPanel;
